import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=checking, false=guest, object=authed
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("cvolve_token");
    if (!token) {
      setUser(false);
      setReady(true);
      return;
    }
    api
      .get("/auth/me")
      .then((res) => setUser(res.data))
      .catch(() => {
        localStorage.removeItem("cvolve_token");
        setUser(false);
      })
      .finally(() => setReady(true));
  }, []);

  const login = useCallback(async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem("cvolve_token", data.token);
      setUser(data.user);
      return { ok: true, user: data.user };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) };
    }
  }, []);

  const register = useCallback(async (payload) => {
    try {
      const { data } = await api.post("/auth/register", payload);
      if (data.require_otp) {
        return { ok: true, require_otp: true, email: payload.email, message: data.message };
      }
      if (data.token && data.user) {
        localStorage.setItem("cvolve_token", data.token);
        setUser(data.user);
      }
      return { ok: true, user: data.user };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) };
    }
  }, []);

  const verifyOtp = useCallback(async (email, otp) => {
    try {
      const { data } = await api.post("/auth/verify-otp", { email, otp });
      localStorage.setItem("cvolve_token", data.token);
      setUser(data.user);
      return { ok: true, user: data.user };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) };
    }
  }, []);

  const resendOtp = useCallback(async (email) => {
    try {
      const { data } = await api.post("/auth/resend-otp", { email });
      return { ok: true, message: data.message };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) };
    }
  }, []);

  const updateProfile = useCallback(async (payload) => {
    const { data } = await api.patch("/profile", payload);
    setUser(data);
    return data;
  }, []);

  const refreshBalance = useCallback(async () => {
    try {
      const token = localStorage.getItem("cvolve_token");
      if (!token) return;
      const { data } = await api.get("/credits/balance");
      if (data) {
        setUser((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            credits: data.credits !== undefined ? data.credits : prev.credits,
            plan: data.plan || prev.plan,
            wallet: data
          };
        });
      }
      return data;
    } catch (_) {}
  }, []);

  // Global Stripe Payment Completion Listener
  useEffect(() => {
    const token = localStorage.getItem("cvolve_token");
    if (!token) return;

    const params = new URLSearchParams(window.location.search);
    const payment = params.get("payment");
    const sessionId = params.get("session_id");

    if (payment === "success" && sessionId) {
      api.post("/payments/verify-session", { session_id: sessionId })
        .then((res) => {
          if (res.data?.paid) {
            const newCredits = res.data?.credits ?? res.data?.balance?.total;
            const newPlan = res.data?.plan ?? res.data?.balance?.plan;
            setUser((prev) => {
              if (!prev) return prev;
              return {
                ...prev,
                credits: newCredits !== undefined ? newCredits : prev.credits,
                plan: newPlan || prev.plan,
              };
            });
            toast.success("Payment confirmed! Credits have been instantly added to your wallet.");
            refreshBalance();
          }
        })
        .catch((err) => {
          console.error("Payment session verification:", err);
        })
        .finally(() => {
          const url = new URL(window.location.href);
          url.searchParams.delete("payment");
          url.searchParams.delete("session_id");
          window.history.replaceState({}, document.title, url.pathname + (url.search ? url.search : ""));
        });
    }
  }, [refreshBalance]);

  // Window Focus & Visibility Sync (Instantly syncs when returning to the tab)
  useEffect(() => {
    const handleSync = () => {
      if (document.visibilityState === "visible") {
        refreshBalance();
      }
    };
    window.addEventListener("focus", handleSync);
    document.addEventListener("visibilitychange", handleSync);
    return () => {
      window.removeEventListener("focus", handleSync);
      document.removeEventListener("visibilitychange", handleSync);
    };
  }, [refreshBalance]);

  const logout = useCallback(() => {
    localStorage.removeItem("cvolve_token");
    setUser(false);
  }, []);

  return (
    <AuthContext.Provider value={{ user, ready, login, register, verifyOtp, resendOtp, logout, updateProfile, refreshBalance }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
